import { ChangeEventHandler, PropsWithChildren } from 'react';
import { Country, CountryRowDefaultValues } from '@/core/types';
import CountryLabel from '@/components/CountryLabel/CountryLabel';
import { COACell } from '@/components/COACell';
import { isCustom } from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import { ControlledCollapse } from '@/components/Collapse/ControlledCollapse';
import classNames from 'classnames';
import Arrow from '@/icons/select-chevron.svg?react';
import { useTranslation } from 'react-i18next';

interface FieldProps {
  value: string;
  error: string;
  touched: boolean;
}

interface Props {
  vatRateHigh: FieldProps;
  vatRateMiddle: FieldProps;
  vatRateLow: FieldProps;
  b2bBuyerVatNumberNotNull: FieldProps;
  arrivalCountryNotEU: FieldProps;
  country: Country;
  onChangeCountryField: (
    code: string,
    field: string
  ) => ChangeEventHandler<HTMLInputElement>;
  onBlurCountryField: (field: string) => ChangeEventHandler<HTMLInputElement>;
  isEditing: boolean;
  defaultNumbers: CountryRowDefaultValues | undefined;
  initialOpened?: boolean;
  comminglingBuyTaxable: FieldProps;
  comminglingBuyTaxExempt: FieldProps;
}

export const COACountryCollapse = ({
  vatRateHigh,
  vatRateMiddle,
  vatRateLow,
  country,
  onChangeCountryField,
  onBlurCountryField,
  isEditing,
  defaultNumbers,
  b2bBuyerVatNumberNotNull,
  arrivalCountryNotEU,
  children,
  initialOpened,
  comminglingBuyTaxable,
  comminglingBuyTaxExempt,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  return (
    <ControlledCollapse
      initialOpened={initialOpened}
      triggerRenderer={(isOpened, onTriggerClick) => {
        return (
          <div
            className='flex cursor-pointer items-center justify-between gap-x-4 px-4 py-3'
            onClick={onTriggerClick}
          >
            <CountryLabel
              country={country.short_name}
              className='font-semibold'
            />
            <div className='flex items-center gap-x-2'>
              {!isOpened &&
                (!!defaultNumbers?.commingling_buy_taxable ||
                  !!defaultNumbers?.commingling_buy_tax_exempt) && (
                  <div>Commingling</div>
                )}
              <Arrow
                className={classNames('SellerPastPeriodsFiling__arrow', {
                  'SellerPastPeriodsFiling__arrow--active': isOpened,
                })}
              />
            </div>
          </div>
        );
      }}
      contentRenderer={() => (
        <div className='px-4'>
          <div className='text-[12px] uppercase leading-4 text-gray-700'>
            {t('Turnover in EU-country by VAT rate')}
          </div>
          {children}
          <div className='flex items-center justify-between gap-x-4'>
            <div className='pb-4 font-semibold'>{t('Regular')}</div>
            <COACell
              error={vatRateHigh.error}
              touched={vatRateHigh.touched}
              testId={`${country.short_name}-vat_rate_high`}
              onChange={onChangeCountryField(
                country.short_name,
                'vat_rate_high'
              )}
              onBlur={onBlurCountryField(`${country.short_name}.vat_rate_high`)}
              value={vatRateHigh.value}
              isEditing={isEditing}
              name={`${country.short_name}-vat_rate_high`}
              isCustom={isCustom(
                vatRateHigh.value,
                defaultNumbers?.vat_rate_high
              )}
              notAvailable={!defaultNumbers?.vat_rate_high}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='pb-4 font-semibold'>{t('Reduced')} 1</div>
            <COACell
              error={vatRateMiddle.error}
              touched={vatRateMiddle.touched}
              testId={`${country.short_name}-vat_rate_middle`}
              onChange={onChangeCountryField(
                country.short_name,
                'vat_rate_middle'
              )}
              onBlur={onBlurCountryField(
                `${country.short_name}.vat_rate_middle`
              )}
              value={vatRateMiddle.value}
              isEditing={isEditing}
              name={`${country.short_name}-vat_rate_middle`}
              isCustom={isCustom(
                vatRateMiddle.value,
                defaultNumbers?.vat_rate_middle
              )}
              notAvailable={!defaultNumbers?.vat_rate_middle}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='pb-4 font-semibold'>{t('Reduced')} 2</div>
            <COACell
              error={vatRateLow.error}
              touched={vatRateLow.touched}
              testId={`${country.short_name}-vat_rate_low`}
              onChange={onChangeCountryField(
                country.short_name,
                'vat_rate_low'
              )}
              onBlur={onBlurCountryField(`${country.short_name}.vat_rate_low`)}
              value={vatRateLow.value}
              isEditing={isEditing}
              name={`${country.short_name}-vat_rate_low`}
              isCustom={isCustom(
                vatRateLow.value,
                defaultNumbers?.vat_rate_low
              )}
              notAvailable={!defaultNumbers?.vat_rate_low}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
          <div className='text-[12px] uppercase leading-4 text-gray-700'>
            {t('Turnover EU')}
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='pb-4 font-semibold'>EU1⟶EU2</div>
            <COACell
              error={b2bBuyerVatNumberNotNull.error}
              touched={b2bBuyerVatNumberNotNull.touched}
              testId={`${country.short_name}-b2b_buyer_vat_number_not_null`}
              onChange={onChangeCountryField(
                country.short_name,
                'b2b_buyer_vat_number_not_null'
              )}
              onBlur={onBlurCountryField(
                `${country.short_name}.b2b_buyer_vat_number_not_null`
              )}
              value={b2bBuyerVatNumberNotNull.value}
              isEditing={isEditing}
              name={`${country.short_name}-b2b_buyer_vat_number_not_null`}
              isCustom={isCustom(
                b2bBuyerVatNumberNotNull.value,
                defaultNumbers?.b2b_buyer_vat_number_not_null
              )}
              notAvailable={!defaultNumbers?.b2b_buyer_vat_number_not_null}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
          <div className='flex items-center justify-between gap-x-4'>
            <div className='pb-4 font-semibold'>EU⟶NON-EU</div>
            <COACell
              error={arrivalCountryNotEU.error}
              touched={arrivalCountryNotEU.touched}
              testId={`${country.short_name}-arrival_country_not_EU`}
              onChange={onChangeCountryField(
                country.short_name,
                'arrival_country_not_EU'
              )}
              onBlur={onBlurCountryField(
                `${country.short_name}.arrival_country_not_EU`
              )}
              value={arrivalCountryNotEU.value}
              isEditing={isEditing}
              name={`${country.short_name}-arrival_country_not_EU`}
              isCustom={isCustom(
                arrivalCountryNotEU.value,
                defaultNumbers?.arrival_country_not_EU
              )}
              notAvailable={!defaultNumbers?.arrival_country_not_EU}
              inputWrapperClassName={`min-h-[66px] flex items-center`}
            />
          </div>
          {(defaultNumbers?.commingling_buy_taxable ||
            defaultNumbers?.commingling_buy_tax_exempt) && (
            <>
              <div className='text-[12px] uppercase leading-4 text-gray-700'>
                Commingling
              </div>
              {defaultNumbers?.commingling_buy_tax_exempt && (
                <div className='flex items-center justify-between gap-x-4'>
                  <div className='pb-4 font-semibold'>
                    {t('Buy (tax-exempt)')}
                  </div>
                  <COACell
                    error={comminglingBuyTaxExempt.error}
                    touched={comminglingBuyTaxExempt.touched}
                    testId={`${country.short_name}-commingling_buy_tax_exempt`}
                    onChange={onChangeCountryField(
                      country.short_name,
                      'commingling_buy_tax_exempt'
                    )}
                    onBlur={onBlurCountryField(
                      `${country.short_name}.commingling_buy_tax_exempt`
                    )}
                    value={comminglingBuyTaxExempt.value}
                    isEditing={isEditing}
                    name={`${country.short_name}-commingling_buy_tax_exempt`}
                    isCustom={isCustom(
                      comminglingBuyTaxExempt.value,
                      defaultNumbers?.commingling_buy_tax_exempt
                    )}
                    notAvailable={!defaultNumbers?.commingling_buy_tax_exempt}
                    inputWrapperClassName={`min-h-[66px] flex items-center`}
                  />
                </div>
              )}
              {defaultNumbers?.commingling_buy_taxable && (
                <div className='flex items-center justify-between gap-x-4'>
                  <div className='pb-4 font-semibold'>{t('Buy (taxable)')}</div>
                  <COACell
                    error={comminglingBuyTaxable.error}
                    touched={comminglingBuyTaxable.touched}
                    testId={`${country.short_name}-commingling_buy_taxable`}
                    onChange={onChangeCountryField(
                      country.short_name,
                      'commingling_buy_taxable'
                    )}
                    onBlur={onBlurCountryField(
                      `${country.short_name}.commingling_buy_taxable`
                    )}
                    value={comminglingBuyTaxable.value}
                    isEditing={isEditing}
                    name={`${country.short_name}-commingling_buy_taxable`}
                    isCustom={isCustom(
                      comminglingBuyTaxable.value,
                      defaultNumbers?.commingling_buy_taxable
                    )}
                    notAvailable={!defaultNumbers?.commingling_buy_taxable}
                    inputWrapperClassName={`min-h-[66px] flex items-center`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    />
  );
};
